import { StreamEvent } from 'types/stream';
import { chatApi } from '../chat-api';
import { BaseQueryApi, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { transformMessageResponse } from './transformMessageResponse';
import { transformDate } from 'lib/utils/common';
import { Chat, Message, ModelConversation } from 'types/chat';
import { createMessageClientId } from './createMessageClientId';

type StreamError = Extract<FetchBaseQueryError, { status: 'CUSTOM_ERROR' }>;
type ModelDeltaEvent = Extract<StreamEvent, { type: 'delta' }>;

type PendingDelta = {
  scope: ModelDeltaEvent['scope'];
  mode: ModelDeltaEvent['mode'];
  chunks: string[];
};

const STREAM_UPDATE_INTERVAL_MS = 100;

export const streamEventHandler = (
  api: BaseQueryApi,
  initialChatId: string,
) => {
  let chatId = initialChatId;
  let streamError: StreamError | null = null;
  const pendingDeltas = new Map<string, PendingDelta>();
  let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const flushPendingDeltas = () => {
    if (flushTimeoutId !== null) {
      clearTimeout(flushTimeoutId);
      flushTimeoutId = null;
    }

    if (pendingDeltas.size === 0) return;

    const deltas = Array.from(pendingDeltas.values(), (pendingDelta) => ({
      ...pendingDelta,
      delta: pendingDelta.chunks.join(''),
    }));
    pendingDeltas.clear();

    api.dispatch(
      chatApi.util.updateQueryData('getChat', chatId, (draft) => {
        deltas.forEach((event) => {
          const targetModel = findModelByMode(draft.models, event.mode);
          if (!targetModel || targetModel.status === 'disabled') return;

          const targetMessage = getTargetMessage(
            targetModel.messages,
            draft.mode,
            targetModel,
          );

          if (targetMessage) {
            targetMessage.text += event.delta;
          }

          if (draft.mode === 'dialogue' && event.scope === 'expert') {
            const commonModel =
              draft.models.find((model) => model.expertRole === 'summary') ??
              draft.models[0];
            if (!commonModel) return;

            // ensurePendingAssistant(commonModel);
            // const commonLast = commonModel.messages.at(-1);
            // if (
            //   commonLast?.role === 'assistant' &&
            //   commonLast.id === null
            // ) {
            //   commonLast.text += event.delta;
            // }
            const commonTargetMessage = getTargetMessage(
              commonModel.messages,
              draft.mode,
              targetModel,
            );

            if (commonTargetMessage) {
              commonTargetMessage.text += event.delta;
            }
          }
        });
      }),
    );
  };

  const enqueueDelta = (event: ModelDeltaEvent) => {
    const key = `${event.scope}:${event.mode.id}:${event.mode.expertRole}`;
    const pendingDelta = pendingDeltas.get(key);

    if (pendingDelta) {
      pendingDelta.chunks.push(event.delta);
    } else {
      pendingDeltas.set(key, {
        scope: event.scope,
        mode: event.mode,
        chunks: [event.delta],
      });
    }

    if (flushTimeoutId === null) {
      flushTimeoutId = setTimeout(
        flushPendingDeltas,
        STREAM_UPDATE_INTERVAL_MS,
      );
    }
  };

  const handleEvent = (event: StreamEvent) => {
    switch (event.type) {
      case 'start': {
        if (event.scope === 'stream') {
          chatId = event.chat.chatId;
          api.dispatch(
            chatApi.util.updateQueryData('getChat', chatId, (draft) => {
              draft.name = event.chat.name;
              draft.date = event.chat.updatedAt;
              draft.additional = event.chat.additional;
              draft.jurisdiction = event.chat.jurisdiction;
              draft.themeOptions = event.chat.customFields;
              draft.mode = event.chat.expertise;
            }),
          );
          api.dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              const chat = draft.find((c) => c.id === chatId);
              if (chat) {
                chat.name = event.chat.name;
                chat.date = event.chat.updatedAt;
                chat.mode = event.chat.expertise;
                chat.models = event.chat.expertModes.map((model) => ({
                  id: model.id,
                  name: model.name,
                  expertRole: model.expertRole,
                  order: model.order,
                  status: model.modeStatus,
                }));
              } else {
                draft.unshift({
                  id: chatId,
                  name: event.chat.name,
                  date: event.chat.updatedAt,
                  isPinned: event.chat.isPinned,
                  projectId: event.chat.projectId,
                  mode: event.chat.expertise,
                  models: event.chat.expertModes.map((model) => ({
                    id: model.id,
                    name: model.name,
                    expertRole: model.expertRole,
                    order: model.order,
                    status: model.modeStatus,
                  })),
                });
              }
            }),
          );
          break;
        }

        // model-level start
        if (event.scope === 'expert' || event.scope === 'summary') {
          api.dispatch(
            chatApi.util.updateQueryData('getChat', chatId, (draft) => {
              const model =
                findModelByMode(draft.models, event.mode) ?? draft.models[0];
              if (!model || model.status === 'disabled') return;

              let lastUserMessage = null;
              for (let i = model.messages.length - 1; i >= 0; i--) {
                const message = model.messages[i];
                if (message.role === 'user' && message.id === null) {
                  lastUserMessage = message;
                  break;
                }
              }

              if (lastUserMessage) lastUserMessage.id = event.userMessageId;

              // ensurePendingAssistant(model);
            }),
          );
        }
        break;
      }
      case 'delta': {
        // model-level delta
        if (event.scope === 'expert' || event.scope === 'summary') {
          enqueueDelta(event);
        }
        break;
      }
      case 'done': {
        flushPendingDeltas();

        if (event.scope === 'stream') {
          api.dispatch(
            chatApi.util.updateQueryData('getChat', chatId, (draft) => {
              if (event.chat) {
                draft.name = event.chat.name;
                draft.date = event.chat.updatedAt;
                draft.isPinned = event.chat.isPinned;
                draft.projectId = event.chat.projectId;
                draft.additional = event.chat.additional;
                draft.jurisdiction = event.chat.jurisdiction;
                draft.themeOptions = event.chat.customFields;
                draft.mode = event.chat.expertise;
                // draft.models = transformModelsConversation(
                //   event.chat.expertModes,
                // );
              }
            }),
          );

          api.dispatch(
            chatApi.util.updateQueryData('getChats', undefined, (draft) => {
              if (!event.chat) return;
              const chat = draft.find((c) => c.id === event.chat.chatId);
              if (!chat) return;
              chat.name = event.chat.name;
              chat.date = event.chat.updatedAt;
              chat.mode = event.chat.expertise;
              chat.models = event.chat.expertModes.map((model) => ({
                id: model.id,
                name: model.name,
                expertRole: model.expertRole,
                order: model.order,
                status: model.modeStatus,
              }));
            }),
          );
          break;
        }

        // model-level done
        if (event.scope === 'expert' || event.scope === 'summary') {
          api.dispatch(
            chatApi.util.updateQueryData('getChat', chatId, (draft) => {
              const model =
                findModelByMode(draft.models, event.mode) ?? draft.models[0];

              if (!model) return;

              const finalMessage = transformMessageResponse(event.message);
              // const last = model.messages.at(-1);

              // if (last?.role === 'assistant' && last.id === null) {
              //   last.id = finalMessage.id;
              //   // last.text = finalMessage.text;
              //   last.rating = finalMessage.rating;
              //   last.date = finalMessage.date;
              //   last.time = finalMessage.time;
              // } else if (finalMessage.role === 'assistant') {
              //   model.messages.push(finalMessage);
              // }
              const lastMessage = getTargetMessage(
                model.messages,
                draft.mode,
                model,
              );

              if (lastMessage) {
                lastMessage.id = finalMessage.id;
                // last.text = finalMessage.text;
                lastMessage.rating = finalMessage.rating;
                lastMessage.date = finalMessage.date;
                lastMessage.time = finalMessage.time;
                lastMessage.attachments = finalMessage.attachments;
              } else if (
                draft.mode !== 'dialogue' &&
                finalMessage.role === 'assistant'
              ) {
                model.messages.push(finalMessage);
              }

              if (draft.mode === 'dialogue' && event.scope === 'expert') {
                const commonModel =
                  draft.models.find((m) => m.expertRole === 'summary') ??
                  draft.models[0];
                if (!commonModel) return;

                // const commonLast = commonModel.messages.at(-1);
                // if (
                //   commonLast?.role === 'assistant' &&
                //   commonLast.id === null
                // ) {
                //   commonLast.id = finalMessage.id;
                //   // commonLast.text = finalMessage.text;
                //   commonLast.rating = finalMessage.rating;
                //   commonLast.date = finalMessage.date;
                //   commonLast.time = finalMessage.time;
                // }

                // // Новый bubble для следующей модели
                // ensurePendingAssistant(commonModel);
                const commonLastMessage = getTargetMessage(
                  commonModel.messages,
                  draft.mode,
                  model,
                );
                if (commonLastMessage) {
                  commonLastMessage.id = finalMessage.id;
                  // commonTargetMessage.text = finalMessage.text;
                  commonLastMessage.rating = finalMessage.rating;
                  commonLastMessage.date = finalMessage.date;
                  commonLastMessage.time = finalMessage.time;
                  commonLastMessage.attachments = finalMessage.attachments;
                }
              }
            }),
          );
        }
        break;
      }
      case 'ping':
        break;
      case 'error':
        flushPendingDeltas();
        streamError = {
          status: 'CUSTOM_ERROR',
          error: event.error,
          data: {
            error: event.error,
            technicalError: event.technicalError,
          },
        };
        break;
    }
  };

  return {
    handleEvent,
    flushPendingDeltas,
    dispose: () => {
      if (flushTimeoutId !== null) {
        clearTimeout(flushTimeoutId);
        flushTimeoutId = null;
      }
      pendingDeltas.clear();
    },
    getStreamError: () => streamError,
    getCurrentChatId: () => chatId,
  };
};

function getTargetMessage(
  messages: Message[],
  mode: Chat['mode'],
  model: ModelConversation,
) {
  if (mode !== 'dialogue') return messages.at(-1) ?? null;

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role === 'user') continue;

    const messageModel = message.model;
    if (
      message.id === null &&
      messageModel.id === model.id &&
      messageModel.expertRole === model.expertRole
    ) {
      return message;
    }
  }
  return null;
}

function findModelByMode(
  models: Array<ModelConversation>,
  mode: { id: string; expertRole: 'summary' | 'expert' },
) {
  return models.find(
    (model) => model.id === mode.id && model.expertRole === mode.expertRole,
  );
}

function _ensurePendingAssistant(model: ModelConversation) {
  const last = model.messages.at(-1);
  const hasPendingAssistant = last?.role === 'assistant' && last.id === null;
  if (hasPendingAssistant) return;
  const { date, time } = transformDate(new Date().toISOString());

  model.messages.push({
    clientId: createMessageClientId(),
    id: null,
    role: 'assistant',
    text: '',
    rating: null,
    date,
    time,
    model: model,
  });
}
